// The charter request's quote form ("Add quote" / "Edit quote"): its state, watchers, queries,
// create/update mutations and effects (the live price breakdown, the margin and the duration estimate).
// Rendered by QuoteFormModal.
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App as AntdApp, Form } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';

import {
  createCharterQuote,
  listAircraft,
  listOperators,
  previewCharterQuote,
  updateCharterQuote,
} from '../../api/endpoints';
import type {
  AirportHit,
  CharterQuote,
  CharterQuoteBreakdown,
  CharterQuoteInput,
  CharterRequestDetail,
} from '../../api/types';
import { useSingleSubmit } from '../../hooks/useSingleSubmit';
import { instantToWall, wallToInstant, zoneForCode } from '../../data/airportTime';
import { aircraftCurrencyToApply, routeChanged, routeKey } from '../../data/quoteFormRules';

/**
 * Called by the page right after the request query, where these hooks always
 * sat; `r` is the request (undefined while it loads).
 */
export function useQuoteForm({
  id,
  r,
  invalidate,
}: {
  id: string;
  r: CharterRequestDetail | undefined;
  invalidate: () => void;
}) {
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CharterQuote | null>(null);
  const [operatorId, setOperatorId] = useState<string | undefined>(undefined);
  // SHAR-604 — the calculation shown under the price fields, produced by the
  // server so it matches what will be saved.
  const [breakdown, setBreakdown] = useState<CharterQuoteBreakdown | null>(null);
  // Admin regression 6 Oct — what the server said about the figures typed (a
  // price or a total over the £999,999,999.99 ceiling). It used to be swallowed:
  // the breakdown vanished and nothing said why.
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Copilot review — clearing the debounce timer does not stop a request that
  // is already in flight. Each preview takes a number; an answer is applied
  // only if no newer preview has started since, so a slow, older response can
  // neither show a stale error nor clear the current one.
  const previewSeq = useRef(0);
  // SHAR-600/603 — the picked airports, kept so the form can show city and
  // country and work out the distance for the duration estimate.
  const [originAp, setOriginAp] = useState<AirportHit | null>(null);
  const [destAp, setDestAp] = useState<AirportHit | null>(null);
  const watchedAircraftId = Form.useWatch('aircraftId', form);
  const watchedOperatorPrice = Form.useWatch('operatorPriceAmount', form);
  const watchedMargin = Form.useWatch('marginGbp', form);
  // SHAR-604 (retest) — has the admin set the margin THEMSELVES? Until they do,
  // the field follows the house rate as the operator price and the currency
  // change; the moment they type one, it is theirs and nothing overwrites it.
  // A ref, not state: it must not cause a render, and the effect below has to
  // read the value it has right now rather than the one from its own closure.
  const marginTouched = useRef(false);
  /**
   * SHAR-603 (fourth retest) — has the ADMIN set the duration?
   *
   * The estimate used to be written only while the field was empty, which is a
   * different question and the reason this came back: the first aircraft filled
   * the field, and from then on it was no longer empty, so picking a second
   * aircraft left the first one's flight time on screen. An hour that belongs
   * to another aeroplane is worse than a blank.
   *
   * "Empty" cannot tell an estimate apart from a typed value; a flag can. Same
   * device the margin has used since SHAR-604.
   */
  const durationTouched = useRef(false);
  const watchedDeparture = Form.useWatch('departureDateTime', form);
  // SHAR-1192 — the departure is typed on the ORIGIN airport's clock and the
  // arrival shown on the destination's. The zone comes from the airport just
  // picked, else from what the request or the quote being edited carry for
  // that code. None of them on an older API: the browser's clock, as before.
  const watchedOrigin = Form.useWatch('originCode', form);
  const watchedDest = Form.useWatch('destCode', form);
  const originTz = zoneForCode(watchedOrigin, originAp, [
    [editing?.originCode, editing?.departureZoned?.timezone],
    [r?.originCode, r?.departureZoned?.timezone],
  ]);
  const destTz = zoneForCode(watchedDest, destAp, [[editing?.destCode, editing?.arrivalZoned?.timezone]]);
  const watchedDuration = Form.useWatch('flightDurationHours', form);
  // SHAR-1332 - the route the form was opened on (or last settled on), to tell a changed
  // airport from the airports arriving with the form.
  const routeRef = useRef('');
  // SHAR-1332 (the currency suspicion) - the aircraft whose currency is already in the
  // field: opening a saved quote must not overwrite the currency it was saved with.
  const ccyAircraftRef = useRef<string | null>(null);
  // SHAR-633 — the CURRENCY has to be watched, not just read. The breakdown
  // effect below called form.getFieldValue('operatorPriceCcy') inside itself but
  // did not depend on it, so changing the currency after entering a price never
  // re-ran the calculation and the block went on showing the amount converted at
  // the old currency - which, starting from GBP, looks exactly like "no
  // conversion is applied at all".
  const watchedCcy = Form.useWatch('operatorPriceCcy', form);

  const operatorsQ = useQuery({ queryKey: ['operators', 'all'], queryFn: () => listOperators({ page: 1, pageSize: 100 }) });
  const aircraftQ = useQuery({
    queryKey: ['aircraft', 'by-op', operatorId],
    queryFn: () => listAircraft({ operatorId, pageSize: 100 }),
    enabled: !!operatorId,
  });

  const createMut = useMutation({
    mutationFn: (body: CharterQuoteInput) => createCharterQuote(id, body),
    onSuccess: () => { message.success('Quote created'); setModalOpen(false); invalidate(); },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Create failed'),
  });
  const updateMut = useMutation({
    mutationFn: ({ qid, body }: { qid: string; body: CharterQuoteInput }) => updateCharterQuote(qid, body),
    onSuccess: () => { message.success('Quote updated'); setModalOpen(false); invalidate(); },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Update failed'),
  });
  // Regression 28 Sep — one submit at a time on this page's forms.
  const submitGuard = useSingleSubmit();
  useEffect(() => {
    if (modalOpen && r && !editing) {
      form.setFieldsValue({ originCode: r.originCode, destCode: r.destCode });
    }
  }, [modalOpen, r, editing, form]);

  // SHAR-599/605 — the aircraft behind the chosen tail number. Its particulars
  // are displayed and its currency is what the operator's price is entered in.
  const selectedAircraft = (aircraftQ.data?.items ?? []).find((a) => a.id === watchedAircraftId);

  // SHAR-605 — the operator prices in their own currency, so the field follows
  // the aircraft rather than being fixed to GBP.
  useEffect(() => {
    const ccy = aircraftCurrencyToApply(selectedAircraft, ccyAircraftRef.current);
    if (ccy) {
      ccyAircraftRef.current = selectedAircraft!.id;
      form.setFieldsValue({ operatorPriceCcy: ccy });
    }
  }, [selectedAircraft, form]);

  // SHAR-1332 - a changed airport changes the route, and the duration belongs to the route:
  // the estimate is taken up again even over a duration the admin typed (Pavel, 21 Oct).
  // Only a CHANGE counts: the airports arriving with the form do not reset a saved duration.
  useEffect(() => {
    if (!modalOpen || !watchedOrigin || !watchedDest) return;
    const key = routeKey(watchedOrigin, watchedDest);
    if (routeChanged(routeRef.current, key)) durationTouched.current = false;
    routeRef.current = key;
  }, [modalOpen, watchedOrigin, watchedDest]);

  // SHAR-603 — the flight duration is NO LONGER computed here. It used to be a
  // great-circle calculation in the browser from originAp/destAp, and those two
  // are only set when the admin picks an airport by hand. On a quote form the
  // route arrives with the request and is never re-picked, so both stayed null,
  // the effect returned on its first guard and the field was left empty - which
  // is exactly what QA reported. The server has the coordinates and the cruise
  // speed sitting next to each other, so it computes it and the preview returns
  // it; see quotedFlightTimeHours in the API. That also removes a second copy of
  // the formula, which could have drifted from the one on the request detail.

  // SHAR-604 — recompute the breakdown as the admin types. Debounced because it
  // is a round trip, and it is a round trip because the exchange rate and the
  // processing fee both live on the server.
  useEffect(() => {
    if (!modalOpen) return;
    const t = setTimeout(async () => {
      const seq = ++previewSeq.current;
      // SHAR-603 (third retest) — THE PRICE IS NOT A PRECONDITION FOR THE
      // DURATION. This guard used to read `if (watchedOperatorPrice == null)
      // return`, which is why the field stayed empty through three retests: QA
      // selects the operator and the tail number and looks at the duration
      // BEFORE typing a price, so the request that carries the estimate was
      // never sent. The server-side calculation was correct and was simply
      // never reached - the same shape of mistake as the browser-side version
      // it replaced.
      //
      // The duration needs the route and the aircraft; the money needs the
      // price. So the call goes out as soon as EITHER is available, and each
      // half of the answer is applied only when its own input is present.
      const hasPrice = watchedOperatorPrice != null;
      const hasAircraft = !!watchedAircraftId;
      if (!hasPrice && !hasAircraft) { setBreakdown(null); setPreviewError(null); return; }
      try {
        const b = await previewCharterQuote({
          operatorPriceAmount: hasPrice ? Number(watchedOperatorPrice) : undefined,
          operatorPriceCcy: watchedCcy || undefined,
          marginGbp: watchedMargin != null ? Number(watchedMargin) : undefined,
          preAuthPaidGbp: r?.preAuthAmountGbp ?? undefined,
          // SHAR-994 — the fee inside the hold: only its fare part comes off.
          preAuthFixedFeeGbp: r?.preAuthFixedFeeGbp ?? 0,
          preAuthExpired: r?.preAuthExpired,
          requestId: r?.id,
          aircraftId: watchedAircraftId || undefined,
          // SHAR-1332 - the duration is for the route in the form, not the request's
          originCode: watchedOrigin || undefined,
          destCode: watchedDest || undefined,
        });
        // Without a price every money line would be a zero the admin never
        // entered, so the breakdown block stays hidden until there is one.
        if (seq !== previewSeq.current) return; // a newer preview has started
        setBreakdown(hasPrice ? b : null);
        setPreviewError(null);
        // a refusal from the last Create / Save described figures that have
        // since changed and now price cleanly: it no longer applies
        createMut.reset();
        updateMut.reset();
        // SHAR-604 (retest) — the margin FIELD tracks the calculation, not just
        // the block below it. QA's point was that the number the admin can see
        // and the number that gets saved were two different things: the block
        // moved with the operator price while the input sat at its first value.
        //
        // Writing it into the field rather than only into the breakdown matters
        // because the margin is saved with the quote - what the admin sees has
        // to be what is stored. It stops the moment they type their own value,
        // including a deliberate zero, which marginTouched records.
        if (hasPrice && !marginTouched.current && b.suggestedMarginGbp > 0) {
          const current = form.getFieldValue('marginGbp');
          if (current !== b.suggestedMarginGbp) {
            form.setFieldsValue({ marginGbp: b.suggestedMarginGbp });
          }
        }
        // SHAR-603 — the estimate follows the aircraft until the admin takes it
        // over. A typed value is theirs and is never overwritten; anything this
        // effect put there is replaced when the aircraft changes, because it
        // describes the aeroplane that is no longer selected.
        if (!durationTouched.current && b.flightDurationHours != null) {
          const current = form.getFieldValue('flightDurationHours');
          if (current !== b.flightDurationHours) {
            form.setFieldsValue({ flightDurationHours: b.flightDurationHours });
          }
        }
      } catch (e: any) {
        if (seq !== previewSeq.current) return; // a newer preview has started
        setBreakdown(null);
        // a 400 carries a sentence for the admin; anything else stays silent,
        // as before (the save will say what is wrong)
        setPreviewError(e?.response?.status === 400 ? (e?.response?.data?.message ?? null) : null);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [modalOpen, watchedOperatorPrice, watchedMargin, watchedCcy, watchedAircraftId, watchedOrigin, watchedDest, r, form]);

  /** Admin regression 6 Oct — a reopened modal starts without the last error. */
  const clearErrors = () => {
    setPreviewError(null);
    createMut.reset();
    updateMut.reset();
  };

  const openCreate = (r: CharterRequestDetail) => {
    clearErrors();
    setEditing(null);
    // SHAR-604 - a new quote starts with the margin following the house rate again.
    marginTouched.current = false;
    // SHAR-603 - and with the duration following the aircraft again.
    durationTouched.current = false;
    routeRef.current = routeKey(r.originCode, r.destCode);
    ccyAircraftRef.current = null;
    setOperatorId(undefined);
    setOriginAp(null);
    setDestAp(null);
    form.resetFields();
    form.setFieldsValue({
      originCode: r.originCode,
      destCode: r.destCode,
      // SHAR-601 — a quote answers a specific request, so it opens on that
      // request's departure rather than an empty picker the admin retypes.
      departureDateTime: r.departureDate
        ? dayjs(`${r.departureDate} ${r.departureTime ?? '00:00'}`, 'YYYY-MM-DD HH:mm')
        : undefined,
      // SHAR-606 — quotes are valid for a week unless the admin says otherwise.
      expiryDate: dayjs().add(7, 'day'),
    });
    setModalOpen(true);
  };
  const openEdit = (q: CharterQuote) => {
    clearErrors();
    setEditing(q);
    // SHAR-604 - a saved quote's margin was chosen by whoever wrote it, so the
    // field must not start following the house rate again on reopen.
    marginTouched.current = q.marginGbp != null;
    // SHAR-603 - a saved quote's duration was decided when it was written, so
    // reopening it must not start re-estimating over the top of it.
    durationTouched.current = q.flightDurationHours != null;
    routeRef.current = routeKey(q.originCode, q.destCode);
    // the saved currency is the quote's own: the aircraft's is applied only if the aircraft changes
    ccyAircraftRef.current = q.operatorPriceCcy ? (q.aircraftId ?? null) : null;
    setOperatorId(q.operatorId ?? undefined);
    setOriginAp(null);
    setDestAp(null);
    form.setFieldsValue({
      operatorId: q.operatorId ?? undefined,
      // SHAR-611 — the form field is aircraftId; only tailNumber was being set,
      // so the Aircraft select opened empty on every edit and silently cleared
      // the aircraft when the admin saved without touching it.
      aircraftId: q.aircraftId ?? undefined,
      tailNumber: q.tailNumber ?? undefined,
      originCode: q.originCode,
      destCode: q.destCode,
      // SHAR-1192 — on the origin airport's clock
      departureDateTime: instantToWall(q.departureDateTime, q.departureZoned?.timezone),
      flightDurationHours: q.flightDurationHours ?? undefined,
      // SHAR-604/605 — reopen on the parts the admin entered, not just the total.
      operatorPriceAmount: q.operatorPriceAmount ?? undefined,
      operatorPriceCcy: q.operatorPriceCcy ?? undefined,
      marginGbp: q.marginGbp ?? undefined,
      priceGbp: q.priceGbp,
      expiryDate: q.expiryDate ? dayjs(q.expiryDate) : undefined,
    });
    setModalOpen(true);
  };

  const submit = submitGuard.wrap(async () => {
    const v = await form.validateFields();
    const ac = aircraftQ.data?.items.find((a) => a.id === v.aircraftId);
    const body: CharterQuoteInput = {
      operatorId: v.operatorId || undefined,
      aircraftId: v.aircraftId || undefined,
      tailNumber: ac?.tailNumber || v.tailNumber || undefined,
      originCode: (v.originCode || '').toUpperCase(),
      destCode: (v.destCode || '').toUpperCase(),
      departureDateTime: wallToInstant(v.departureDateTime as Dayjs, originTz),
      flightDurationHours: v.flightDurationHours ?? undefined,
      // SHAR-604/605 — the parts go up and the server derives the total from
      // them; priceGbp is sent as the last preview so an older server still
      // receives something sane.
      operatorPriceAmount: v.operatorPriceAmount ?? undefined,
      operatorPriceCcy: v.operatorPriceCcy ?? undefined,
      marginGbp: v.marginGbp ?? undefined,
      priceGbp: breakdown?.totalGbp ?? v.priceGbp ?? 0,
      expiryDate: v.expiryDate ? (v.expiryDate as Dayjs).toISOString() : undefined,
    };
    if (editing) await updateMut.mutateAsync({ qid: editing.id, body });
    else await createMut.mutateAsync(body);
  });

  return {
    form,
    modalOpen,
    setModalOpen,
    editing,
    operatorId,
    setOperatorId,
    breakdown,
    previewError,
    setOriginAp,
    setDestAp,
    marginTouched,
    durationTouched,
    watchedDeparture,
    watchedDuration,
    originTz,
    destTz,
    operatorsQ,
    aircraftQ,
    createMut,
    updateMut,
    submitGuard,
    selectedAircraft,
    openCreate,
    openEdit,
    submit,
  };
}

export type QuoteForm = ReturnType<typeof useQuoteForm>;
